import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Container, ThemeProvider } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import MagicLinkSent from '@/pages/Onboarding/components/MagicLinkSent/MagicLinkSent';
import {
  Product,
  getTheme,
  getProductKey,
  getLob,
} from '@/utils/productHelpers';
import { activateEmail } from '@/utils/sparkActivationApi';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import LimbotHoldingPhone from '@/assets/LimbotHoldingPhone.svg';
import SleepioThumbsUp from '@/assets/SleepioThumbsUp.svg';

const PRODUCT_HERO: Record<string, { src: string; alt: string }> = {
  spark: { src: LimbotHoldingPhone, alt: 'Spark Limbot holding phone' },
  sleepio: { src: SleepioThumbsUp, alt: 'Sleepio Thumbs Up' },
};

interface MagicLinkSentLocationState {
  email?: string;
  accessCode?: string;
  organizationSlug?: string;
  limbixUserId?: string;
  isDependent?: boolean;
}

const MagicLinkSentPage: React.FC = () => {
  const { product } = useParams<{ product: Product }>();
  const { state: locationState } = useLocation<MagicLinkSentLocationState>();
  const { t } = useTranslation();
  const productKey = getProductKey(product);
  const hero = PRODUCT_HERO[productKey] || PRODUCT_HERO.sleepio;

  const handleResend = async (email: string) => {
    try {
      await activateEmail({
        email,
        product,
        accessCode: locationState?.accessCode,
        organizationSlug: locationState?.organizationSlug,
        lob: getLob(locationState?.organizationSlug),
        limbixUserId: locationState?.limbixUserId,
        ...(locationState?.isDependent ? { isDependent: true } : {}),
      });
      return { success: true };
    } catch {
      return { success: false };
    }
  };

  const isDependent = locationState?.isDependent ?? false;

  return (
    <ThemeProvider theme={getTheme(product)}>
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <MagicLinkSent
            imageSrc={hero.src}
            imageAlt={hero.alt}
            title={
              isDependent
                ? t(
                  `coverage.pre_onboarding.magic_link_sent.${productKey}.dependent_title`,
                )
                : t(
                  `coverage.pre_onboarding.magic_link_sent.${productKey}.title`,
                )
            }
            description={
              isDependent
                ? t(
                  `coverage.pre_onboarding.magic_link_sent.${productKey}.dependent_description`,
                )
                : t(
                  `coverage.pre_onboarding.magic_link_sent.${productKey}.description`,
                )
            }
            cantFindTitle={t(
              `coverage.pre_onboarding.magic_link_sent.${productKey}.cant_find_title`,
            )}
            cantFindDescription={t(
              `coverage.pre_onboarding.magic_link_sent.${productKey}.cant_find_description`,
            )}
            email={locationState?.email || ''}
            onResend={handleResend}
            buttonLabel={t(
              `coverage.pre_onboarding.magic_link_sent.${productKey}.button`,
            )}
            hideResend={isDependent}
          />
          <ConsentWidget />
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

export default MagicLinkSentPage;
